import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { CardRadio } from '@/components/ui/CardRadio'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  EndorsementStaffAction,
  endorsementSteps,
  type InternalEndorsement,
  useEndorsementStep,
} from '@/models/internalEndorsement'
import { PolicyChangeAskKind } from '@/models/policyChange'
import { confirmStep } from './confirmStep'

const KIND_COPY: Record<PolicyChangeAskKind, { title: string; description: string }> = {
  [PolicyChangeAskKind.REPLY]: {
    title: 'Reply or send a document',
    description: 'The request stays on its step, quotes and all. They answer on the request page.',
  },
  [PolicyChangeAskKind.CHANGE_REQUEST]: {
    title: 'Change the request',
    description: 'It goes back to them to edit and send again. Quotes entered so far are cleared.',
  },
}

/** Ask the customer for something: a reply or document at any step, or a change before it is quoted. */
export function AskCustomerDialog({
  endorsement,
  disabled,
}: {
  endorsement: InternalEndorsement
  disabled: boolean
}) {
  const ask = useEndorsementStep(endorsementSteps.ask)
  const kinds = [
    endorsement.actions.includes(EndorsementStaffAction.ASK_REPLY) && PolicyChangeAskKind.REPLY,
    endorsement.actions.includes(EndorsementStaffAction.ASK) && PolicyChangeAskKind.CHANGE_REQUEST,
  ].filter((kind) => kind !== false)
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState(kinds[0] ?? PolicyChangeAskKind.REPLY)
  const [question, setQuestion] = useState('')
  const [needsDocument, setNeedsDocument] = useState(false)

  async function confirm() {
    const saved = await confirmStep(
      ask.mutateAsync({ id: endorsement.id, question: question.trim(), kind, needsDocument }),
      'Asked the customer.'
    )
    if (saved) {
      setOpen(false)
      setQuestion('')
      setNeedsDocument(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(opening) => {
        // What staff can ask moves with the request's step.
        if (opening) setKind(kinds[0] ?? PolicyChangeAskKind.REPLY)
        setOpen(opening)
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" variant="outline" disabled={disabled}>
          Ask customer
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ask about {endorsement.idShort}</DialogTitle>
          <DialogDescription>
            The customer gets an email and sees your question on their request.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {kinds.length > 1 && (
            <div className="flex flex-col gap-2">
              {kinds.map((option) => (
                <CardRadio key={option} isSelected={kind === option} onSelect={() => setKind(option)}>
                  <div>{KIND_COPY[option].title}</div>
                  <div className="text-xs font-normal text-neutral-600">{KIND_COPY[option].description}</div>
                </CardRadio>
              ))}
            </div>
          )}
          {kinds.length === 1 && <p className="text-sm text-neutral-600">{KIND_COPY[kind].description}</p>}
          <div className="flex flex-col gap-2">
            <Label htmlFor="endorsement-question">What do they need to do?</Label>
            <Textarea
              id="endorsement-question"
              rows={3}
              value={question}
              disabled={ask.isPending}
              onChange={(event) => setQuestion(event.target.value)}
            />
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={needsDocument}
              onCheckedChange={(checked) => setNeedsDocument(checked === true)}
            />
            They need to upload a document
          </label>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!question.trim()}
            onClick={() => void confirm()}
            loading={ask.isPending ? 'Saving…' : undefined}
          >
            Ask customer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
